import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isValidYearMonth } from '../../utils/dateHelpers';

const MonthYearPicker = ({
  value,
  onChange,
  placeholder = 'Select month',
  allowPresent = false,
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => {
    if (isValidYearMonth(value)) return parseInt(value.slice(0, 4), 10);
    return new Date().getFullYear();
  });
  const [present, setPresent] = useState(() => value === 'Present');
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 280 });

  useEffect(() => {
    const handleDocDown = (e) => {
      const c = containerRef.current;
      const p = popoverRef.current;
      const target = e.target;
      if (!c) return;
      if (c.contains(target) || (p && p.contains(target))) return; // inside
      setOpen(false);
    };
    document.addEventListener('mousedown', handleDocDown);
    document.addEventListener('touchstart', handleDocDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleDocDown);
      document.removeEventListener('touchstart', handleDocDown);
    };
  }, []);

  const updateCoords = () => {
    const c = containerRef.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const width = Math.max(260, Math.min(320, r.width));
    let top = r.bottom + 6;
    let left = r.left;
    const maxLeft = window.innerWidth - width - 8;
    if (left > maxLeft) left = maxLeft;
    // Prevent off-screen top
    if (top + 300 > window.innerHeight) {
      // place above if not enough space below
      top = Math.max(8, r.top - 8 - 280);
    }
    setCoords({ top, left, width });
  };

  useEffect(() => {
    if (!open) return;
    updateCoords();
    const onScroll = () => updateCoords();
    const onResize = () => updateCoords();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  useEffect(() => {
    if (value === 'Present') setPresent(true);
    else setPresent(false);
  }, [value]);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const selectedMonthIndex = isValidYearMonth(value) ? parseInt(value.slice(5, 7), 10) - 1 : -1;

  const selectMonth = (idx) => {
    const ym = `${year}-${String(idx + 1).padStart(2, '0')}`;
    onChange?.(ym);
    setOpen(false);
  };

  const togglePresent = (checked) => {
    setPresent(checked);
    if (checked) {
      onChange?.('Present');
      setOpen(false);
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.('');
    setPresent(false);
    setOpen(false);
  };

  const display = value === 'Present' ? 'Present' : (isValidYearMonth(value) ? value : '');

  return (
    <div
      className={`paragraph_normal_regular flex h-[49px] items-center justify-between rounded-md border border-solid ${
        open ? 'border-gray-500' : 'border-gray-300'
      } bg-gray-100 px-4 py-3 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer focus-within:border-gray-500'}`}
      ref={containerRef}
      role="button"
      tabIndex={0}
      aria-haspopup="dialog"
      aria-expanded={open}
      onMouseDown={(e) => {
        if (disabled) return;
        e.preventDefault();
        setOpen(true);
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen(true);
        }
      }}
    >
      <span className={display ? 'text-gray-700' : 'text-gray-400 italic'}>
        {display || placeholder}
      </span>
      <svg width="16" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 text-gray-500">
        <path fillRule="evenodd" clipRule="evenodd" d="M5.69844 0.400391C5.38018 0.400391 5.07495 0.526819 4.84991 0.751862C4.62487 0.976906 4.49844 1.28213 4.49844 1.60039V2.80039H3.29844C2.66192 2.80039 2.05147 3.05325 1.60138 3.50333C1.15129 3.95342 0.898438 4.56387 0.898438 5.20039V17.2004C0.898438 17.8369 1.15129 18.4474 1.60138 18.8974C2.05147 19.3475 2.66192 19.6004 3.29844 19.6004H17.6984C18.335 19.6004 18.9454 19.3475 19.3955 18.8974C19.8456 18.4474 20.0984 17.8369 20.0984 17.2004V5.20039C20.0984 4.56387 19.8456 3.95342 19.3955 3.50333C18.9454 3.05325 18.335 2.80039 17.6984 2.80039H16.4984V1.60039C16.4984 1.28213 16.372 0.976906 16.147 0.751862C15.9219 0.526819 15.6167 0.400391 15.2984 0.400391C14.9802 0.400391 14.675 0.526819 14.4499 0.751862C14.2249 0.976906 14.0984 1.28213 14.0984 1.60039V2.80039H6.89844V1.60039C6.89844 1.28213 6.77201 0.976906 6.54697 0.751862C6.32192 0.526819 6.0167 0.400391 5.69844 0.400391ZM5.69844 6.40039C5.38018 6.40039 5.07495 6.52682 4.84991 6.75186C4.62487 6.97691 4.49844 7.28213 4.49844 7.60039C4.49844 7.91865 4.62487 8.22388 4.84991 8.44892C5.07495 8.67396 5.38018 8.80039 5.69844 8.80039H15.2984C15.6167 8.80039 15.9219 8.67396 16.147 8.44892C16.372 8.22388 16.4984 7.91865 16.4984 7.60039C16.4984 7.28213 16.372 6.97691 16.147 6.75186C15.9219 6.52682 15.6167 6.40039 15.2984 6.40039H5.69844Z" fill="currentColor"/>
      </svg>
      {open && !disabled && createPortal(
        <div
          ref={popoverRef}
          className="date-picker-popover"
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, zIndex: 10000 }}
        >
          <div className="date-picker-header">
            <button
              className="date-picker-nav-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setYear(y => y - 1);
              }}
              aria-label="Previous year"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="date-picker-year">{year}</div>
            <button
              className="date-picker-nav-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setYear(y => y + 1);
              }}
              aria-label="Next year"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="date-picker-months">
            {months.map((m, idx) => (
              <div
                key={m}
                className={`date-picker-month ${selectedMonthIndex === idx ? 'selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  selectMonth(idx);
                }}
              >
                {m}
              </div>
            ))}
          </div>
          <div className="date-picker-footer">
            <button
              className="clear-date-btn"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clear(e);
              }}
            >
              Clear
            </button>
            {allowPresent && (
              <label
                className="present-toggle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <input
                  type="checkbox"
                  checked={present || value === 'Present'}
                  onChange={(e) => togglePresent(e.target.checked)}
                />
                Present
              </label>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MonthYearPicker;
